// EmailLogView — auditoria de envios via Resend.
//
// Casos de uso:
//   - "Anna disse que não recebeu o reset de senha ontem" → busca email dela,
//     filtra por kind=password-reset, vê se ok=true e quando.
//   - "Quantos convites de signup falharam essa semana?" → kind=signup-invite,
//     ok=false.
//
// Antes dessa view não havia log estruturado — só console.warn do Worker,
// descartado pelo Cloudflare em algumas horas.

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  Mail,
  RefreshCw,
  Search,
  XCircle
} from "lucide-react";
import { Skeleton } from "@app/ui/components/skeleton";
import { call } from "./api";
import { friendlyError } from "@app/ui/lib/friendly-error";

interface LogRow {
  id: string;
  toEmail: string;
  subject: string;
  kind: string;
  fromEmail: string | null;
  ok: boolean;
  providerId: string | null;
  reason: string | null;
  triggeredByUserId: string | null;
  triggeredByEmail: string | null;
  meta: Record<string, unknown>;
  sentAt: string;
}

interface SummaryRow {
  kind: string;
  total: number;
  failed: number;
}

interface LogResponse {
  rows: LogRow[];
  summary: SummaryRow[];
  since: string;
  limit: number;
}

const phase = signal<"loading" | "ready" | "error">("loading");
const rows = signal<LogRow[]>([]);
const summary = signal<SummaryRow[]>([]);
const errMsg = signal<string | null>(null);
const q = signal("");
const kindFilter = signal("");
const okFilter = signal<"" | "true" | "false">("");
const sinceDays = signal(7);

async function load(silent = false) {
  if (!silent) phase.value = "loading";
  errMsg.value = null;
  try {
    const since = new Date(Date.now() - sinceDays.value * 24 * 60 * 60 * 1000);
    const params = new URLSearchParams();
    if (q.value.trim()) params.set("q", q.value.trim());
    if (kindFilter.value) params.set("kind", kindFilter.value);
    if (okFilter.value) params.set("ok", okFilter.value);
    params.set("since", since.toISOString());
    params.set("limit", "300");
    const res = await call<LogResponse>(
      `/api/manager/email-log?${params.toString()}`
    );
    rows.value = res.rows;
    summary.value = res.summary;
    phase.value = "ready";
  } catch (e) {
    errMsg.value = friendlyError(e);
    if (!silent) phase.value = "error";
  }
}

export function EmailLogView() {
  useEffect(() => {
    void load();
    // load roda em mudanças de filtro também (via useEffect abaixo)
  }, []);

  return (
    <div class="space-y-4">
      <div class="rounded-md border border-blue-500/30 bg-blue-500/5 px-4 py-3 text-sm">
        <Mail className="mr-1 inline size-4 text-blue-600" />
        <strong>Auditoria de emails.</strong> Cada envio via Resend é registrado
        aqui (sucesso ou falha). Use os filtros pra investigar reports de "não
        recebi email". Histórico anterior à migração 0051 não está disponível —
        confira o dashboard do Resend pra esses casos.
      </div>

      <Filters />

      {phase.value === "loading" && (
        <div class="space-y-2">
          <Skeleton class="h-12 w-full" />
          <Skeleton class="h-12 w-full" />
          <Skeleton class="h-12 w-full" />
        </div>
      )}
      {phase.value === "error" && (
        <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          <AlertCircle className="mr-2 inline size-4" />
          {errMsg.value}
        </div>
      )}
      {phase.value === "ready" && (
        <>
          <SummaryStrip />
          <LogTable />
        </>
      )}
    </div>
  );
}

function Filters() {
  const [draftQ, setDraftQ] = useState(q.value);
  return (
    <div class="flex flex-wrap items-end gap-2 rounded-md border border-border bg-card/40 p-3">
      <div class="flex-1 min-w-[220px]">
        <label class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Email destino
        </label>
        <div class="relative">
          <Search
            width="1em"
            height="1em"
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="search"
            value={draftQ}
            onInput={(e) => setDraftQ((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                q.value = draftQ;
                void load();
              }
            }}
            placeholder="anna@…  (Enter pra buscar)"
            class="w-full rounded-md border border-border bg-background pl-8 pr-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </div>
      </div>
      <div>
        <label class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Tipo
        </label>
        <select
          value={kindFilter.value}
          onChange={(e) => {
            kindFilter.value = (e.target as HTMLSelectElement).value;
            void load();
          }}
          class="rounded-md border border-border bg-background px-2 py-1.5 text-xs"
          aria-label="Filtrar por tipo"
        >
          <option value="">Todos os tipos</option>
          <option value="password-reset">Reset de senha</option>
          <option value="signup-invite">Convite de signup</option>
          <option value="signup-interest">Manifestação aprovada</option>
          <option value="charge-notification">Cobrança</option>
          <option value="manager-test">Teste do admin</option>
          <option value="generic">Genérico</option>
        </select>
      </div>
      <div>
        <label class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Status
        </label>
        <select
          value={okFilter.value}
          onChange={(e) => {
            okFilter.value = (e.target as HTMLSelectElement).value as "" | "true" | "false";
            void load();
          }}
          class="rounded-md border border-border bg-background px-2 py-1.5 text-xs"
          aria-label="Filtrar por status"
        >
          <option value="">Todos</option>
          <option value="true">Apenas OK</option>
          <option value="false">Apenas falhas</option>
        </select>
      </div>
      <div>
        <label class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Janela
        </label>
        <select
          value={String(sinceDays.value)}
          onChange={(e) => {
            sinceDays.value = parseInt((e.target as HTMLSelectElement).value, 10);
            void load();
          }}
          class="rounded-md border border-border bg-background px-2 py-1.5 text-xs"
          aria-label="Período"
        >
          <option value="1">Últimas 24h</option>
          <option value="3">3 dias</option>
          <option value="7">7 dias</option>
          <option value="30">30 dias</option>
          <option value="90">90 dias</option>
        </select>
      </div>
      <button
        type="button"
        onClick={() => {
          q.value = draftQ;
          void load();
        }}
        class="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110"
      >
        <Search width="0.95em" height="0.95em" />
        Buscar
      </button>
      <button
        type="button"
        onClick={() => void load()}
        title="Recarregar"
        aria-label="Recarregar"
        class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-3 py-1.5 text-xs hover:bg-accent"
      >
        <RefreshCw width="0.95em" height="0.95em" />
      </button>
    </div>
  );
}

function SummaryStrip() {
  const list = summary.value;
  if (list.length === 0) return null;
  const total = list.reduce((s, x) => s + x.total, 0);
  const failed = list.reduce((s, x) => s + x.failed, 0);
  return (
    <div class="flex flex-wrap items-center gap-2">
      <div class="rounded-md border border-border bg-card/60 px-3 py-1.5 text-xs">
        <span class="font-bold">{total}</span> envios
      </div>
      {failed > 0 && (
        <div class="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-1.5 text-xs text-destructive">
          <span class="font-bold">{failed}</span> falharam
        </div>
      )}
      {list.map((s) => (
        <div
          key={s.kind}
          class="rounded-md border border-border bg-background px-2.5 py-1 text-[11px]"
        >
          <span class="font-semibold">{s.kind}</span>:{" "}
          <span>{s.total}</span>
          {s.failed > 0 && (
            <span class="ml-1 text-destructive">({s.failed} ✗)</span>
          )}
        </div>
      ))}
    </div>
  );
}

function LogTable() {
  const list = rows.value;
  if (list.length === 0) {
    return (
      <div class="rounded-md border border-dashed border-border bg-background/40 px-4 py-10 text-center">
        <p class="text-sm text-muted-foreground">
          Nenhum envio encontrado nesse filtro.
        </p>
      </div>
    );
  }
  return (
    <div class="overflow-x-auto rounded-md border border-border">
      <table class="w-full text-sm">
        <thead class="bg-muted/40 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          <tr>
            <th class="px-3 py-2 text-left">Status</th>
            <th class="px-3 py-2 text-left">Quando</th>
            <th class="px-3 py-2 text-left">Tipo</th>
            <th class="px-3 py-2 text-left">Para</th>
            <th class="px-3 py-2 text-left">Assunto</th>
            <th class="px-3 py-2 text-left">Motivo / ID</th>
          </tr>
        </thead>
        <tbody>
          {list.map((r) => (
            <LogRow key={r.id} row={r} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LogRow({ row }: { row: LogRow }) {
  return (
    <tr class="border-t border-border align-top">
      <td class="px-3 py-2">
        {row.ok ? (
          <span class="inline-flex items-center gap-1 rounded-md bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 className="size-3" /> OK
          </span>
        ) : (
          <span class="inline-flex items-center gap-1 rounded-md bg-destructive/15 px-2 py-0.5 text-[10px] font-semibold text-destructive">
            <XCircle className="size-3" /> Falha
          </span>
        )}
      </td>
      <td class="px-3 py-2 text-[11px] text-muted-foreground whitespace-nowrap">
        {formatDateTime(row.sentAt)}
      </td>
      <td class="px-3 py-2">
        <code class="rounded bg-muted px-1.5 py-0.5 text-[10px]">{row.kind}</code>
      </td>
      <td class="px-3 py-2 font-mono text-[11px]">{row.toEmail}</td>
      <td class="px-3 py-2 max-w-md">
        <div class="truncate" title={row.subject}>
          {row.subject}
        </div>
      </td>
      <td class="px-3 py-2 max-w-md text-[11px] text-muted-foreground">
        {row.ok ? (
          row.providerId && (
            <code class="font-mono text-[10px]">{row.providerId}</code>
          )
        ) : (
          <span class="text-destructive">{row.reason ?? "(sem motivo)"}</span>
        )}
      </td>
    </tr>
  );
}

function formatDateTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return iso;
  }
}
